/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './ManufacturingClose.module.scss';

type Translate = (key: string) => string;

const getPoints = (t: Translate) => [
  t('Match rate reported against your file, not a curated sample.'),
  t('Registry source and filing date shown on every matched field.'),
  t('Group linkage applied so spend rolls up to the parent.'),
  t('ISO 27001 certified. Registered with the UK ICO.'),
];

export const ManufacturingClose = () => {
  const { t } = useInt();
  const ref = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(ref, { threshold: 0.2 });

  return (
    <Section className={styles.root}>
      <div className={cn(styles.block, revealed && styles.blockIn)} ref={ref}>
        <div className={styles.grid}>
          <div className={styles.copy}>
            <TitleBlock
              title={t('Bring a slice of your vendor master.')}
              description={t(
                'Send a few thousand rows. We return them matched to registration numbers, linked to their corporate groups, with duplicates flagged, so you can see the match rate against your own data before any commercial conversation.',
              )}
              align="left"
            />
            <div className={styles.actions}>
              <Button type="blue" size="xl" href="/book-a-demo" suffixIcon="arrow-forward">
                {t('Book a demo')}
              </Button>
              <Button type="dark-outline" size="xl" href="/free-trial">
                {t('Start a free trial')}
              </Button>
            </div>
          </div>

          <ul className={styles.points}>
            {getPoints(t).map((point, i) => (
              <li className={styles.point} style={{ '--d': `${i * 90}ms` } as CSSProperties} key={point}>
                <span className={styles.check}>
                  <svg
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3.5 8.5 6.5 11.5 12.5 4.5" />
                  </svg>
                </span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Section>
  );
};
